import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { RoomBookingData, BookingInvitee } from '../../../types/RoomBooking'

// ── Booking info sent by server on room entry ────────────────────────────────

export interface ActiveRoomBookingInfo {
  objectId: string
  virtualRoomId: string
  roomName?: string
  title: string
  creatorId: string
  creatorName: string
  startTime: string
  endTime: string
  invitees: BookingInvitee[]
  isInvitee: boolean
  isActive: boolean
}

// ── Wrap-up payload from server ──────────────────────────────────────────────

export interface BookingWrapUpInfo {
  objectId: string
  virtualRoomId: string
  roomName?: string
  title: string
  creatorName: string
  startTime: string
}

// ── State ────────────────────────────────────────────────────────────────────

interface RoomBookingState {
  /** Booking info for the virtual room the player is currently in */
  activeRoomBooking: ActiveRoomBookingInfo | null
  /** Bookings created by the user */
  myBookings: RoomBookingData[]
  /** Bookings the user is invited to */
  myInvites: RoomBookingData[]
  /** Today's bookings where the current user is creator or invitee (fetched on room join) */
  myTodaysBookings: RoomBookingData[]
  /** Wrap-up notification for non-invitees in a booked room */
  wrapUpNotification: BookingWrapUpInfo | null
  /** Whether the booking form dialog is open */
  bookingFormOpen: boolean
  /** Pre-selected room when opening from VR menu */
  bookingFormPreselectedRoom: { roomId: string; roomName: string } | null
  /** Set when editing an existing booking */
  bookingFormEditingId: string | null
  /** Company ID for the booking form (used pre-game when room state is empty) */
  bookingFormCompanyId: string | null
  /** Whether "My Bookings" panel is open */
  myBookingsPanelOpen: boolean
  /** Loading state for async operations */
  loading: boolean
  /** Incremented after create/edit/cancel to trigger list refresh */
  bookingsRefreshTrigger: number
}

const initialState: RoomBookingState = {
  activeRoomBooking: null,
  myBookings: [],
  myInvites: [],
  myTodaysBookings: [],
  wrapUpNotification: null,
  bookingFormOpen: false,
  bookingFormPreselectedRoom: null,
  bookingFormEditingId: null,
  bookingFormCompanyId: null,
  myBookingsPanelOpen: false,
  loading: false,
  bookingsRefreshTrigger: 0,
}

// ── Slice ────────────────────────────────────────────────────────────────────

export const roomBookingSlice = createSlice({
  name: 'roomBooking',
  initialState,
  reducers: {
    // Server-pushed booking info on room entry
    setActiveRoomBooking: (state, action: PayloadAction<ActiveRoomBookingInfo>) => {
      state.activeRoomBooking = action.payload
    },
    clearActiveRoomBooking: (state) => {
      state.activeRoomBooking = null
    },

    // My bookings & invites (fetched from Parse)
    setMyBookings: (state, action: PayloadAction<RoomBookingData[]>) => {
      state.myBookings = action.payload
    },
    setMyInvites: (state, action: PayloadAction<RoomBookingData[]>) => {
      state.myInvites = action.payload
    },
    setMyTodaysBookings: (state, action: PayloadAction<RoomBookingData[]>) => {
      state.myTodaysBookings = action.payload
    },

    // Wrap-up toast
    setWrapUpNotification: (state, action: PayloadAction<BookingWrapUpInfo>) => {
      state.wrapUpNotification = action.payload
    },
    clearWrapUpNotification: (state) => {
      state.wrapUpNotification = null
    },

    // Booking form dialog
    openBookingForm: (
      state,
      action: PayloadAction<{
        preselectedRoom?: { roomId: string; roomName: string } | null
        editingId?: string | null
        companyId?: string | null
      }>,
    ) => {
      state.bookingFormOpen = true
      state.bookingFormPreselectedRoom = action.payload.preselectedRoom ?? null
      state.bookingFormEditingId = action.payload.editingId ?? null
      state.bookingFormCompanyId = action.payload.companyId ?? null
    },
    closeBookingForm: (state) => {
      state.bookingFormOpen = false
      state.bookingFormPreselectedRoom = null
      state.bookingFormEditingId = null
      state.bookingFormCompanyId = null
    },

    // My Bookings panel
    openMyBookingsPanel: (state) => {
      state.myBookingsPanelOpen = true
    },
    closeMyBookingsPanel: (state) => {
      state.myBookingsPanelOpen = false
    },

    // Loading
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },

    // Bump to trigger list refresh in panels
    triggerBookingsRefresh: (state) => {
      state.bookingsRefreshTrigger += 1
    },
  },
})

export const {
  setActiveRoomBooking,
  clearActiveRoomBooking,
  setMyBookings,
  setMyInvites,
  setMyTodaysBookings,
  setWrapUpNotification,
  clearWrapUpNotification,
  openBookingForm,
  closeBookingForm,
  openMyBookingsPanel,
  closeMyBookingsPanel,
  setLoading,
  triggerBookingsRefresh,
} = roomBookingSlice.actions

// ── Selectors ───────────────────────────────────────────────────────────────

/**
 * SC-422: a VR belongs to its map and is shared by every room on that map, so a booking is for
 * one room's VR. Today's bookings span all the user's rooms; only the joined room's count here.
 */
const inJoinedRoom = (b: RoomBookingData, room: { customRoomId?: string } | undefined) =>
  !room?.customRoomId || b.customRoomId === room.customRoomId

/**
 * Returns true when the current user is an invitee or creator of an active
 * booking in the given virtual room, based on the locally cached today's
 * bookings. Used client-side to suppress the knock modal for known invitees
 * without waiting for the server round-trip. [SC-324]
 */
export const selectAmIInvitedToRoom = (
  state: { roomBooking: RoomBookingState; user: { userData: { id?: string } | null }; room?: { customRoomId?: string } },
  virtualRoomId: string | undefined
): boolean => {
  if (!virtualRoomId) return false
  const userId = state.user.userData?.id
  if (!userId) return false
  const now = Date.now()
  return state.roomBooking.myTodaysBookings.some((b) => {
    if (b.virtualRoomId !== virtualRoomId || !inJoinedRoom(b, state.room)) return false
    if (b.status === 'CANCELLED') return false
    if (new Date(b.startTime).getTime() > now || new Date(b.endTime).getTime() <= now) return false
    return b.creatorId === userId || b.invitees.some((inv) => inv.userId === userId)
  })
}

/**
 * Returns true when the current user is the creator or an invitee of
 * the given booking (by objectId), based on today's bookings cache.
 */
export const selectAmIParticipantOfBooking = (
  state: { roomBooking: RoomBookingState; user: { userData: { id?: string } | null } },
  bookingId: string | undefined
): boolean => {
  if (!bookingId) return false
  const userId = state.user.userData?.id
  if (!userId) return false
  return state.roomBooking.myTodaysBookings.some((b) => {
    if (b.objectId !== bookingId) return false
    return b.creatorId === userId || b.invitees.some((inv) => inv.userId === userId)
  })
}

/**
 * Returns the active booking for the given virtual room (if any), based on today's bookings cache.
 */
export const selectActiveBookingForRoom = (
  state: { roomBooking: RoomBookingState; room?: { customRoomId?: string } },
  virtualRoomId: string | undefined
): RoomBookingData | undefined => {
  if (!virtualRoomId) return undefined
  const now = Date.now()
  return state.roomBooking.myTodaysBookings.find((b) => {
    if (b.virtualRoomId !== virtualRoomId || !inJoinedRoom(b, state.room)) return false
    if (b.status === 'CANCELLED') return false
    return new Date(b.startTime).getTime() <= now && new Date(b.endTime).getTime() > now
  })
}

export default roomBookingSlice.reducer
